import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { lookupConciliation, lookupTribunalCase, rememberConciliation } from '../../lib/caseLinks';
import { sha256Hex } from '../../lib/hash';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { Conciliation, Grievance } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function GrievanceDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { persona } = useAuth();
  const gq = useApiQuery<Grievance>(`/api/v1/m16/grievances/${id}`);
  const isOfficer = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER);
  const isCaseworkStaff = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER, M16_ROLES.MEDIATOR);
  const isSelfWorker = Boolean(persona?.nin) && persona?.nin === gq.data?.workerNin;

  if (gq.loading) return <Spinner label="Loading grievance…" />;
  if (gq.error) return <ErrorNote message={gq.error} />;
  if (!gq.data) return <ErrorNote message="Not found" />;
  const g = gq.data;

  const linkedConciliationId = lookupConciliation(g.id);
  const linkedTribunalId = lookupTribunalCase(g.id);

  return (
    <div>
      <PageHeader
        title={g.refNo}
        subtitle={`${g.type.replaceAll('_', ' ')} grievance — filed ${formatDate(g.filedAt)}`}
        actions={<StatusBadge status={g.state} />}
      />

      <div className="mb-6 grid gap-5 lg:grid-cols-3">
        <div className="card space-y-3 p-5 lg:col-span-2">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Worker NIN</dt>
              <dd className="font-mono text-slate-800">
                {g.workerNin === 'REDACTED_PENDING_REGISTRATION' ? (
                  <span className="italic text-slate-400">Redacted until registered</span>
                ) : (
                  g.workerNin
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Employer entity</dt>
              <dd className="font-mono text-slate-800">{g.employerEntityId}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Registration deadline</dt>
              <dd className="text-slate-800">{formatDateTime(g.registrationDeadline)}</dd>
            </div>
            <div>
              <dt className="text-xs font-semibold uppercase text-slate-400">Registered</dt>
              <dd className="text-slate-800">
                {g.registeredAt ? (
                  <>
                    {formatDateTime(g.registeredAt)} — <StatusBadge status={g.registeredWithinSla ? 'OK' : 'OVERDUE'} />
                  </>
                ) : (
                  '—'
                )}
              </dd>
            </div>
            {g.withdrawnAt ? (
              <div className="col-span-2">
                <dt className="text-xs font-semibold uppercase text-slate-400">Withdrawn</dt>
                <dd className="text-slate-800">
                  {formatDateTime(g.withdrawnAt)} — {g.withdrawnReason}
                </dd>
              </div>
            ) : null}
          </dl>
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Narrative</div>
            <p className="whitespace-pre-wrap text-sm text-slate-700">{g.narrative}</p>
          </div>

          {g.state === 'CONCILIATION' || g.state === 'SETTLED' ? (
            <div className="border-t border-slate-100 pt-3">
              {linkedConciliationId ? (
                <Link to={`/conciliation/${linkedConciliationId}`} className="btn-secondary">
                  View conciliation case
                </Link>
              ) : (
                <InfoNote message="Conciliation has started for this grievance. This browser doesn't have its case id cached (m16 has no GET /conciliation?grievanceId= lookup) — find it under Conciliation → caseload." />
              )}
            </div>
          ) : null}
          {g.state === 'CERTIFIED_TO_TRIBUNAL' ? (
            <div className="border-t border-slate-100 pt-3">
              {linkedTribunalId ? (
                <Link to={`/tribunal/${linkedTribunalId}`} className="btn-secondary">
                  View tribunal case
                </Link>
              ) : (
                <InfoNote message="This grievance has been certified to tribunal. This browser doesn't have its case id cached (m16 has no GET /tribunal-cases?grievanceId= lookup) — find it under Tribunal Cases." />
              )}
            </div>
          ) : null}
        </div>

        <div className="space-y-4">
          {isOfficer && g.state === 'FILED' ? <RegisterCard grievanceId={g.id} onDone={gq.reload} /> : null}
          {isOfficer && g.state === 'REGISTERED' ? (
            <StartConciliationCard grievanceId={g.id} onStarted={(cId) => navigate(`/conciliation/${cId}`)} />
          ) : null}
          {(isCaseworkStaff || isSelfWorker) && ['FILED', 'REGISTERED', 'CONCILIATION'].includes(g.state) ? (
            <WithdrawCard grievanceId={g.id} onDone={gq.reload} />
          ) : null}
        </div>
      </div>

      <EvidenceSection grievance={g} onAdded={gq.reload} />
    </div>
  );
}

function RegisterCard({ grievanceId, onDone }: { grievanceId: string; onDone: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const register = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/grievances/${grievanceId}/register`, { note: note.trim() || undefined });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Registration failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Register grievance</h2>
      <p className="mb-2 text-xs text-slate-500">Statutory 5-working-day SLA is tracked, not blocked — a late registration still succeeds.</p>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <textarea className="field-input mb-2 min-h-[3rem]" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button className="btn-primary w-full" onClick={register} disabled={busy}>
        {busy ? 'Registering…' : 'Register'}
      </button>
    </div>
  );
}

function StartConciliationCard({ grievanceId, onStarted }: { grievanceId: string; onStarted: (conciliationId: string) => void }) {
  const [mediatorId, setMediatorId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    if (!mediatorId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const conciliation = await api.post<Conciliation>(`/api/v1/m16/grievances/${grievanceId}/conciliation/start`, { mediatorId: mediatorId.trim() });
      rememberConciliation(grievanceId, conciliation.id);
      onStarted(conciliation.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not start conciliation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Start conciliation</h2>
      <p className="mb-2 text-xs text-slate-500">Assigns a mediator; opens a 30-day window for up to 2 sessions.</p>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <Field label="Mediator id" className="mb-2">
        <input className="field-input" value={mediatorId} onChange={(e) => setMediatorId(e.target.value)} placeholder="e.g. mediator-r-lesperance" />
      </Field>
      <button className="btn-primary w-full" onClick={start} disabled={busy || !mediatorId.trim()}>
        {busy ? 'Starting…' : 'Start conciliation'}
      </button>
    </div>
  );
}

function WithdrawCard({ grievanceId, onDone }: { grievanceId: string; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const withdraw = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m16/grievances/${grievanceId}/withdraw`, { reason: reason.trim() });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdrawal failed');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="card p-4">
        <button className="btn-danger w-full" onClick={() => setOpen(true)}>
          Withdraw grievance
        </button>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <h2 className="mb-2 text-sm font-bold text-slate-700">Withdraw grievance</h2>
      {error ? (
        <div className="mb-2">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <textarea className="field-input mb-2 min-h-[3rem]" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <div className="flex gap-2">
        <button className="btn-danger flex-1" onClick={withdraw} disabled={busy || reason.trim().length < 3}>
          {busy ? 'Withdrawing…' : 'Confirm withdraw'}
        </button>
        <button className="btn-secondary" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function EvidenceSection({ grievance, onAdded }: { grievance: Grievance; onAdded: () => void }) {
  const [docRef, setDocRef] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async () => {
    if (!docRef.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const sha256 = await sha256Hex(`${docRef.trim()}|${label.trim()}`);
      await api.post(`/api/v1/m16/grievances/${grievance.id}/evidence`, { docRef: docRef.trim(), label: label.trim() || undefined, sha256 });
      setDocRef('');
      setLabel('');
      onAdded();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not add evidence');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-3 text-sm font-bold text-slate-700">Evidence ({grievance.evidence.length})</h2>
      {grievance.evidence.length === 0 ? (
        <p className="mb-3 text-xs text-slate-400">No evidence attached yet.</p>
      ) : (
        <ul className="mb-3 space-y-2">
          {grievance.evidence.map((e, i) => (
            <li key={i} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
              <div className="font-semibold text-slate-700">{e.label || e.docRef}</div>
              <div className="mt-0.5 font-mono text-[11px] text-slate-400">
                {e.docRef} · sha256 {e.sha256.slice(0, 16)}…
              </div>
              {e.uploadedAt ? <div className="mt-0.5 text-[11px] text-slate-400">Added {formatDateTime(e.uploadedAt)}</div> : null}
            </li>
          ))}
        </ul>
      )}
      {grievance.state === 'WITHDRAWN' ? (
        <InfoNote message="Cannot add evidence to a withdrawn grievance." />
      ) : (
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          {error ? (
            <div className="sm:col-span-3">
              <ErrorNote message={error} />
            </div>
          ) : null}
          <input className="field-input" placeholder="Document reference" value={docRef} onChange={(e) => setDocRef(e.target.value)} />
          <input className="field-input" placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
          <button className="btn-secondary" onClick={add} disabled={busy || !docRef.trim()}>
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      )}
    </div>
  );
}
